import Head from "next/head"

const entries = {
  "/": {
    title: "Sign in to your Seam Console account",
    heading: "Sign in to Seam Console",
    description:
      "Sign in to Seam Console to manage connected devices, access codes, workspaces, and API keys. Find developer documentation or create a Seam account.",
  },
  "/signin": {
    title: "Sign in to your Seam Console account",
    heading: "Sign in to Seam Console",
    description:
      "Sign in to your Seam account to manage connected devices, access codes, workspaces, and API keys in Seam Console.",
  },
  "/signup": {
    title: "Create your Seam Console account",
    heading: "Create your Seam account",
    description:
      "Create a Seam account to connect devices, explore the API in a sandbox workspace, and start building with Seam Console.",
  },
}

function getEntry(pathname: string) {
  return Object.hasOwn(entries, pathname)
    ? entries[pathname as keyof typeof entries]
    : undefined
}

// Keep public navigation metadata outside the app's client-only page tree.
export function PublicEntryMetadata({ pathname }: { pathname: string }) {
  const entry = getEntry(pathname)
  if (!entry) return null
  const url = `https://console.seam.co${pathname}`
  const image = "https://www.seam.co/img/open-graph/og-cover-xl-2.jpg"

  return (
    <Head>
      <title>{entry.title}</title>
      <meta name="description" content={entry.description} />
      <link rel="canonical" href={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Seam" />
      <meta property="og:title" content={entry.title} />
      <meta property="og:description" content={entry.description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={image} />
      <meta
        property="og:image:alt"
        content="Seam — Connect your app to the physical world"
      />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={entry.title} />
      <meta name="twitter:description" content={entry.description} />
      <meta name="twitter:image" content={image} />
    </Head>
  )
}

// Useful navigation while the client application loads, including when
// JavaScript is unavailable. The existing auth flow replaces this on mount.
export function PublicEntryFallback({ pathname }: { pathname: string }) {
  const entry = getEntry(pathname)
  if (!entry) return null

  return (
    <main className="mx-auto my-24 max-w-md space-y-4 px-6 font-sans leading-relaxed">
      <h1 className="text-2xl font-semibold">{entry.heading}</h1>
      <p>{entry.description}</p>
      <p>The secure sign-in form requires JavaScript.</p>
      <nav
        aria-label="Seam account and developer resources"
        className="space-y-3 [&_a]:underline"
      >
        {pathname === "/" && (
          <p>
            <a href="/signin">Sign in to Seam</a>
          </p>
        )}
        <p>
          <a href={pathname === "/signup" ? "/signin" : "/signup"}>
            {pathname === "/signup" ? "Sign in" : "Create an account"}
          </a>
        </p>
        <p>
          <a href="https://www.seam.co/docs/core-concepts/seam-console">
            Seam Console documentation
          </a>
        </p>
      </nav>
    </main>
  )
}
